// Global Instructions Rule Applied!
// Shape of GET /funds/:id/statement-settings and
// GET /fund-groups/:id/statement-settings (view__fund_statement_settings /
// view__fund_group_statement_settings) and of their PATCH routes: what is
// about the investor STATEMENTS of a fund or a consolidated fund rather than
// the fund — the layout, the partners' capital page, the entity statements
// of a consolidated fund and the wording overrides — plus the layouts, the
// statement types with their default wording and, from the statement
// builders, the text blocks they print and the merge fields a block may use.
// What a statement prints about the fund (legal name, address, signatory)
// are funds columns in the fund terms record.
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

const TextBlocksSchema = z.record(z.string(), z.string().nullable())

export const FundStatementSettingsRowSchema = z
  .object({
    // null until the first save
    fund_statement_settings__id: z.string().nullish(),
    // null = the default layout
    fund_statement_settings__statement_layout_id: z.string().nullish(),
    fund_statement_settings__show_partners_capital: z.boolean(),
    // a consolidated fund's setting; carried (true) on a fund's row too
    fund_statement_settings__send_entity_statements: z.boolean(),
    // one merged PDF per account and fund when a run is sent (stage 6)
    fund_statement_settings__merge_on_send: z.boolean(),
    // the run also generates the Quarterly Fund Report
    // (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1)
    fund_statement_settings__include_fund_report: z.boolean(),
    // block key -> the entity's own wording; a missing key keeps the default
    fund_statement_settings__text_overrides: TextBlocksSchema.nullish()
  })
  .strict()

export const StatementTextBlockSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    help: z.string().nullish(),
    // cfg.investor_statement_types.code of the document the block prints on
    document: z.string(),
    // printed only on the statements of a consolidated fund
    consolidated_only: z.boolean()
  })
  .strict()

export const FundStatementSettingsSchema = z
  .object({
    // exactly one of the two: whose settings these are
    fund: z
      .object({
        funds__id: z.string(),
        funds__name: z.string().nullish(),
        funds__legal_name: z.string().nullish()
      })
      .strict()
      .optional(),
    fund_group: z
      .object({
        fund_groups__id: z.string(),
        fund_groups__name: z.string().nullish(),
        fund_groups__legal_name: z.string().nullish()
      })
      .strict()
      .optional(),
    settings: FundStatementSettingsRowSchema,
    layouts: z.array(
      z
        .object({
          id: z.string(),
          code: z.string(),
          label: z.string(),
          description: z.string().nullish(),
          is_default: z.boolean()
        })
        .strict()
    ),
    // the documents, each with the default wording of its blocks
    statement_types: z.array(
      z.object({ id: z.number(), code: z.string(), label: z.string(), text_blocks: TextBlocksSchema.nullish() }).strict()
    ),
    // the layout that applies: the entity's choice, else the default
    prog__layout_id: z.string().nullish(),
    // code-owned, from the statement builders
    text_blocks: z.array(StatementTextBlockSchema),
    merge_fields: z.array(z.object({ key: z.string(), description: z.string() }).strict())
  })
  .strict()

export const FundStatementSettingsRowFields = makeFieldKeys(FundStatementSettingsRowSchema)

export type FundStatementSettings = z.infer<typeof FundStatementSettingsSchema>
export type StatementTextBlock = z.infer<typeof StatementTextBlockSchema>

export function validateFundStatementSettings(data: unknown): FundStatementSettings {
  try {
    return validateWithZodSchema(FundStatementSettingsSchema, data, 'fund statement settings')
  } catch (error) {
    throw new Error(`Fund statement settings validation failed: ${error}`)
  }
}
